const STORAGE_KEY = 'guestAuth'

export const appPath = (path) => {
  let value = String(path || '').split('?')[0]
  if (value === '/view' || value.startsWith('/view/')) {
    value = value.slice(5) || '/'
  }
  if (!value.startsWith('/')) value = `/${value}`
  return value
}

export const isGuestPath = (path) => {
  const value = appPath(path)
  return value === '/guest' || value.startsWith('/guest/')
}

export const isHomepagePath = (path) => {
  const value = appPath(path)
  return value === '/homepage' || value.startsWith('/homepage/')
}

export const isHostAuthPath = (path) => !isGuestPath(path)
const PIN_UNLOCKED_KEY = 'guestPinUnlocked'
const TEN_YEARS = 60 * 60 * 24 * 365 * 10

let pinUnlocked = false

const readJson = (raw) => {
  if (!raw) return null
  try {
    return typeof raw === 'string' ? JSON.parse(raw) : raw
  } catch {
    return null
  }
}

const readLocal = () => {
  if (typeof localStorage === 'undefined') return null
  return readJson(localStorage.getItem(STORAGE_KEY))
}

const writeLocal = (data) => {
  if (typeof localStorage === 'undefined') return
  if (!data) {
    localStorage.removeItem(STORAGE_KEY)
    return
  }
  localStorage.setItem(STORAGE_KEY, JSON.stringify(data))
}

export const useGuestAuth = () => {
  const guestCookie = useCookie(STORAGE_KEY, {
    maxAge: TEN_YEARS,
    sameSite: 'lax',
    path: '/',
    secure: process.env.NODE_ENV === 'production',
  })

  const guestData = computed(() => {
    const local = readLocal()
    if (local?.token) return local
    return readJson(guestCookie.value)
  })

  const token = computed(() => guestData.value?.token || null)
  const familyIdx = computed(() => guestData.value?.familyIdx || null)
  const deviceIdx = computed(() => guestData.value?.deviceIdx || null)
  const hostName = computed(() => guestData.value?.hostName || null)
  const isLinked = computed(() => !!token.value)

  const setGuestAuth = (newToken, extra = {}) => {
    const payload = {
      token: newToken,
      familyIdx: extra.familyIdx ?? guestData.value?.familyIdx ?? null,
      deviceIdx: extra.deviceIdx ?? guestData.value?.deviceIdx ?? null,
      hostName: extra.hostName ?? guestData.value?.hostName ?? null,
    }
    guestCookie.value = JSON.stringify(payload)
    writeLocal(payload)
  }

  const isPinUnlocked = () => pinUnlocked

  const markPinUnlocked = () => {
    pinUnlocked = true
    if (typeof sessionStorage !== 'undefined') {
      sessionStorage.removeItem(PIN_UNLOCKED_KEY)
    }
  }

  const clearPinUnlocked = () => {
    pinUnlocked = false
    if (typeof sessionStorage !== 'undefined') {
      sessionStorage.removeItem(PIN_UNLOCKED_KEY)
    }
  }

  const clearGuestAuth = () => {
    guestCookie.value = null
    writeLocal(null)
    clearPinUnlocked()
  }

  if (import.meta.client) {
    const local = readLocal()
    if (local?.token && !guestCookie.value) {
      guestCookie.value = JSON.stringify(local)
    } else if (!local?.token && guestCookie.value) {
      writeLocal(readJson(guestCookie.value))
    }
  }

  return {
    token,
    familyIdx,
    deviceIdx,
    hostName,
    isLinked,
    setGuestAuth,
    clearGuestAuth,
    isPinUnlocked,
    markPinUnlocked,
    clearPinUnlocked,
  }
}
